/**
 * HOW FAR AHEAD A COACH LETS A CLIENT LOOK — W16's web half.
 *
 * ⚠️ THE FIVE VALUES ARE `coach_clients.visible_range`'s CHECK, and the phone's `VisibleRange`
 * enum carries the same five. Three statements of one vocabulary is two too many, and the repo's
 * rule is that shared vocabularies are GENERATED from one side — but the other two are Swift and
 * SQL, and nothing generates TypeScript from a CHECK today. So the count is asserted instead
 * (`VISIBLE_RANGE_COUNT`), the same device `equipment.ts` uses, and a value the database allows
 * but this file does not would fail that assertion rather than falling silently through a
 * `switch`.
 */

export const VISIBLE_RANGES = ["previous", "current", "next", "plus2", "all"] as const;
export type VisibleRange = (typeof VISIBLE_RANGES)[number];

/** Stated so a LOSSY edit cannot pass a byte comparison against its own output. */
export const VISIBLE_RANGE_COUNT = 5;

/**
 * 🔴 WHAT A LINK GETS WHEN A COACH CREATES IT — and it is NOT the schema default.
 *
 * The COLUMN defaults to `'all'`, which is exactly what every client could see before the column
 * existed: **a product change must never arrive as a column default.** `'next'` is the value a
 * coach would sensibly choose, and the design says the APP writes it — explicitly, at the moment
 * the coach creates the link and the control is on screen — so the narrowing is a choice somebody
 * made while looking at it, not something that happened to an athlete overnight.
 *
 * 🔴 **AND IT HAS NO PRODUCTION READER TODAY. SAID OUT LOUD RATHER THAN LEFT TO BE DISCOVERED.**
 * MEASURED 2026-09-19: the coach's INSERT on `coach_clients` is granted at COLUMN level on
 * exactly five columns — `coach_id`, `invited_email`, `invited_first_name`, `invited_last_name`,
 * `status` — so `invite.ts` **cannot** name `visible_range` at insert time at all; a new link
 * takes the schema's `'all'`. Writing this value with a follow-up UPDATE would be possible (the
 * UPDATE grant does include the column) and is deliberately NOT done: **the Add Client dialog
 * carries no visibility control**, so a silent `'next'` would be exactly the thing the default
 * was chosen to prevent — a narrowing nobody chose, one layer up from the schema.
 *
 * So this constant is a DECISION waiting for its control, not a dead branch: it ships when the
 * dialog has the question on it. Filed as a W-item so it is not rediscovered as a bug.
 */
export const RANGE_FOR_NEW_LINK: VisibleRange = "next";

/**
 * The stored value, or `'all'` for anything unrecognised.
 *
 * ⚠️ `'all'` AND NOT `'next'`, for the same reason the schema defaults that way: `'all'` is
 * BEHAVIOUR-PRESERVING. An old build reading a value added later, or a row written before the
 * migration, must show the athlete what they saw yesterday. A *sensible* value reached by
 * accident is still a week taken from somebody nobody decided about.
 */
export function rangeFromStored(raw: string | null | undefined): VisibleRange {
  return (VISIBLE_RANGES as readonly string[]).includes(raw ?? "")
    ? (raw as VisibleRange)
    : "all";
}

/** The coach's own words for each setting — what the ATHLETE will be able to reach. */
export function rangeLabel(v: VisibleRange): string {
  switch (v) {
    case "previous": return "Last week and this week";
    case "current":  return "This week only";
    case "next":     return "Up to next week";
    case "plus2":    return "Up to two weeks ahead";
    case "all":      return "Any week you have assigned";
  }
}

/**
 * 🔴 THE TILE'S OWN WORDS — **THEIRS, COPIED** (`docs/EVERFIT-MAP.md` §2-ter, read live
 * 2026-09-19; his copy order of ~08:55 and Fable's ruling on it).
 *
 * Everfit's `Date range visible to client` is a segmented row of FOUR tiles reading
 * `Previous Weeks · Current Week · Next Week · +2 Weeks`. Those four words are taken exactly.
 * **The fifth is ours and has to be**: a four-value control cannot express *"any week you have
 * assigned"*, which is what every athlete on this database can reach today — so removing it to
 * match their count would narrow a live behaviour to copy a screen. It is named `Any week` in
 * their register rather than in ours (`Any week you have assigned` was the row label, which was
 * a sentence because a full-width row had room for one; a tile does not).
 *
 * ⚠️ SEPARATE FROM `rangeLabel`, NOT A REPLACEMENT FOR IT. The tile is a NAME on a control; the
 * label is a SENTENCE about what the athlete gets. Folding them into one string would mean the
 * tile either reads like prose or the sentence reads like a button.
 */
export function rangeTile(v: VisibleRange): string {
  switch (v) {
    case "previous": return "Previous Weeks";
    case "current":  return "Current Week";
    case "next":     return "Next Week";
    case "plus2":    return "+2 Weeks";
    case "all":      return "Any week";
  }
}

/**
 * The consequence, in one line, under the choice.
 *
 * ⚠️ IT NAMES WHAT THIS IS NOT. The control gates BROWSING, never access — the athlete's data is
 * exactly as readable as it was, and a coach who thinks they have hidden something would be
 * wrong in a way that matters. Said on the screen rather than in a comment nobody opens.
 */
export function rangeConsequence(v: VisibleRange): string {
  switch (v) {
    case "previous": return "They can look back one week as well as at this one.";
    case "current":  return "No week arrows at all — they see the week they are in.";
    case "next":     return "They can look at next week once you have assigned something to it.";
    case "plus2":    return "They can look two weeks ahead once those weeks have work.";
    case "all":      return "They can reach every week you have assigned, however far ahead.";
  }
}

/**
 * Is this setting a NARROWING of what the athlete can reach today?
 *
 * Used to decide whether the screen warns. ⚠️ Only `'all'` is not — every other value takes
 * something away, and a coach choosing one should be told which, once, rather than discovering it
 * from an athlete's message.
 */
export function narrowsFromToday(v: VisibleRange): boolean {
  return v !== "all";
}
